import { useEffect, useState } from 'react';
import {
  GEWERK_TEXT, api, auswahllisten, zeitpunkt,
  type Auswahllisten,
} from '../api.ts';
import {
  Auswahl, Dialog, Fehlerkasten, HakenReihe, Text, ausFehler,
  type Fehlerlage,
} from './Formular.tsx';
import { Leer } from './Geruest.tsx';
import {
  VORGEFUNDEN_LEER, Vorgefunden, vorgefundenRumpf,
  type VorgefundenEingabe,
} from './Vorgefunden.tsx';
/* DIE RECHNUNG STEHT OHNE JSX DANEBEN. Warum, steht im Kopf jener Datei. */
import {
  mitFremdemWert, technikAusStand, technikGeaendert, technikRumpf,
  type TechnikEingabe, type TechnikStand,
} from './technikfelder.ts';

/**
 * Was von der Baustelle zurückkommt.
 *
 * ORIGINALTEXT MARVIN (08.09.2026)
 *   „hier geht es um die rückmeldungen die der tiefbau draußen macht und
 *    notwendig sind, dass er sie bei sich im auftrag einträgt und anpasst,
 *    diese infos sollen dann ja für alle anderen sichtbar sein, außerdem
 *    braucht die montage neben dem fertig melden und dem einblasstop auch die
 *    möglichkeit über checkboxen bei «Auftrag nicht erledigt» zusammen zu
 *    klicken, was sache ist ... damit der innendienst weiß was er damit
 *    machen soll."
 *
 * ZWEI FENSTER, EINE DATEI, UND SIE GEHÖREN ZUSAMMEN: beide werden von
 * jemandem bedient, der vor dem Loch steht, beide gehen das ganze Haus an,
 * und beide sind KEIN Formular über den Auftrag.
 */


// ============================================================================
// 1. Was der Tiefbau vorfindet
// ============================================================================

/**
 * VIER ANGABEN, JEDE MIT PLANWERT UND ISTWERT.
 *
 * Marvin auf die Rückfrage, welche Meterzahl gemeint ist:
 * > „er korigiert die länge des SNR, rohrfarbe, Verband und rohrstärke, alle
 * >  diese werte stammen von der planung, können bzw werden vor ort aber
 * >  abweichen und wir brauchen alle (Tiefbau, Monteure, innendienst) die
 * >  richtigen angaben, daher ist es essentiell."
 *
 * **DER PLANWERT STEHT NEBEN DEM FELD UND WIRD NICHT VORBELEGT.** Wer ihn in
 * das Feld schriebe, bekäme beim Speichern eine „Rückmeldung", die nur den
 * Planwert wiederholt - und danach ist nicht mehr zu unterscheiden, ob jemand
 * nachgesehen hat oder nur auf Speichern gedrückt. Leer heisst hier: nicht
 * gemeldet, es gilt die Planung.
 */
/**
 * DIE VIER FELDER, UND SIE STEHEN GENAU EINMAL IM HAUS.
 *
 * Sie erscheinen an ZWEI Stellen - im eigenen Fenster „Von der Baustelle
 * zurückmelden" und unten beim „Fertig melden", weil der Tiefbau dort ohnehin
 * steht, wenn er die Baustelle verlässt. **Zwei Abschriften laufen beim ersten
 * Nachtrag auseinander**, und dann fragt dasselbe Formular je nach Weg etwas
 * anderes ab.
 *
 * DIE AUSWAHLLISTEN KOMMEN VON `/api/auswahl` UND WERDEN HIER NICHT
 * NACHGEBAUT. `ROHRVERBAENDE` und `ROEHRCHEN` stehen in
 * `api/src/regeln/auswahl.ts` und NUR dort - eine zweite Liste in der
 * Oberfläche wäre beim ersten neuen Farbcode falsch.
 *
 * **UND SIE SPERREN NICHT.** Ein Wert, der schon dasteht und in keiner Liste
 * steht, wird der Auswahl hinzugefügt (`mitFremdemWert`): der Bauakte-Scan
 * schreibt diese Spalten automatisch, und was in keiner Liste steht, bleibt
 * dort wörtlich stehen. Ohne diese Zeile verlöre das Dropdown ihn beim ersten
 * Öffnen, lautlos.
 */
export function TechnikFelder({
  stand, wert, setze, fehler, gesperrt,
}: {
  stand: (TechnikStand & Record<string, unknown>) | null;
  wert: TechnikEingabe;
  setze: (e: TechnikEingabe) => void;
  fehler?: Fehlerlage | null;
  gesperrt?: boolean;
}) {
  const [listen, setListen] = useState<Auswahllisten | null>(null);

  /* EIN FEHLGESCHLAGENER ABRUF DARF DIE AUSWAHL NICHT LEEREN - dieselbe
     Lehre wie bei den Gruppen am 31.08.2026. Bleibt `listen` leer, steht
     unter dem Feld, dass die Liste gerade nicht da ist, und der schon
     gemeldete Wert bleibt trotzdem wählbar. */
  useEffect(() => { auswahllisten().then(setListen).catch(() => {}); }, []);

  /** Der Planwert als Satz unter dem Feld. Ohne Wert steht dort nichts. */
  const plan = (w: unknown, einheit = '') =>
    w === null || w === undefined || String(w).trim() === ''
      ? 'Aus der Planung ist dazu nichts eingetragen.'
      : `Aus der Planung: ${w}${einheit}`;

  const wahl = (feld: 'rohrverband' | 'roehrchen', w: string) =>
    mitFremdemWert(listen ? listen[feld] : [], w);

  return (
    <>
      <div className="feldreihe">
        <Text name="snr_laenge_ist_m" titel="SNR-Länge (Ist)" art="number"
              wert={wert.laenge} gesperrt={gesperrt} breite={200}
              setze={(w) => setze({ ...wert, laenge: w })}
              fehler={fehler} hinweis={plan(stand?.snr_laenge_m, ' m')} />
        <Text name="snr_roehrchenbreite_ist" titel="Rohrstärke (Ist)"
              wert={wert.breite} gesperrt={gesperrt} breite={200}
              setze={(w) => setze({ ...wert, breite: w })}
              fehler={fehler} hinweis={plan(stand?.roehrchenbreite)} />
      </div>
      <div className="feldreihe">
        <Auswahl name="snr_rohrverband_ist" titel="Rohrverband (Ist)"
                 wert={wert.verband} gesperrt={gesperrt} breite={260}
                 setze={(w) => setze({ ...wert, verband: w })}
                 optionen={wahl('rohrverband', wert.verband)}
                 leerText="wie geplant"
                 fehler={fehler} hinweis={plan(stand?.rohrverband)} />
        <Auswahl name="snr_roehrchen_ist" titel="Röhrchen (Ist)"
                 wert={wert.roehrchen} gesperrt={gesperrt} breite={260}
                 setze={(w) => setze({ ...wert, roehrchen: w })}
                 optionen={wahl('roehrchen', wert.roehrchen)}
                 leerText="wie geplant"
                 fehler={fehler} hinweis={plan(stand?.roehrchen)} />
      </div>

      {/* EINE LISTE, DIE NICHT ANKAM, SAGT ES. Ein Auswahlfeld mit einem
          einzigen Eintrag sieht sonst aus wie eine gepflegte Liste mit einem
          Eintrag darin. */}
      {!listen && (
        <p className="klein leise" style={{ marginTop: -4 }}>
          Die Auswahllisten für Verband und Röhrchen sind gerade nicht
          erreichbar. Was schon gemeldet ist, steht trotzdem zur Wahl.
        </p>
      )}

      {stand?.snr_ist_gemeldet_am != null && (
        <p className="klein leise">
          Zuletzt zurückgemeldet {zeitpunkt(stand.snr_ist_gemeldet_am as string)}
          {stand.snr_ist_name ? ` von ${stand.snr_ist_name}` : ''}.
        </p>
      )}
    </>
  );
}


export function TechnikmeldungDialog({
  auftragId, baustelle, zu, fertig,
}: {
  auftragId: string; baustelle?: string; zu: () => void; fertig: () => void;
}) {
  const [d, setD] = useState<any>(null);
  const [e, setE] = useState<TechnikEingabe>(technikAusStand(null));
  /* UND DIE 3 ANGABEN, DIE DERSELBE TIEFBAU BEANTWORTET.
     ORIGINALTEXT MARVIN (18.09.2026)
       „bei dem abmelden der beim tiefbau fehlen die anderen checkboxen, ich
        sagte doch, das er die luftprüfung durchführen muss, pipe im haus,
        kabel auch im haus, da ist nur die angabe zur pipe drin."
     Sie standen nur im Fenster „Fertig melden". **Wer sich abmeldet,
     beantwortet dieselben Fragen, egal durch welche Tür er geht** - und
     dieses Fenster ist die Tür, die der Tiefbau nimmt. */
  const [vorgefunden, setVorgefunden] =
    useState<VorgefundenEingabe>(VORGEFUNDEN_LEER);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);

  useEffect(() => {
    api.technikmeldung(auftragId).then((s) => {
      setD(s);
      setE(technikAusStand(s));
    }).catch((f) => setFehler(ausFehler(f)));
  }, [auftragId]);

  async function melden() {
    setLaeuft(true); setFehler(null);
    try {
      /* EIN ZUG, EINE ANFRAGE. Die 3 Angaben gehen mit den 4 Pipe-Feldern
         zusammen hinaus; der Server bucht sie mit derselben Regel wie beim
         Fertigmelden (`regeln/vorgefunden.ts`) und VOR den Pipe-Feldern,
         damit die Pflichtordner schon nachgezogen sind, wenn der Tiefbau
         gleich danach fertig meldet. */
      await api.technikMelden(auftragId,
        { ...technikRumpf(e), ...vorgefundenRumpf(vorgefunden) });
      fertig();
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  return (
    <Dialog titel="Von der Baustelle zurückmelden" untertitel={baustelle}
            breite={640} laeuft={laeuft} zu={zu}>
      {!d ? <Leer text="Wird geladen." /> : (
        <>
          <Fehlerkasten lage={fehler} />

          {/* WEM DER WEG NICHT OFFENSTEHT, BEKOMMT DEN GRUND UND KEINEN
              GRAUEN KNOPF. Eine Partnerfirma meldet am EIGENEN Tiefbau
              zurück; der Riegel steht im Server, hier steht der Satz dazu. */}
          {d.darf === false ? (
            <div className="hinweis warnung" style={{ marginTop: 0 }}>
              <b>An diesem Auftrag hat deine Firma den Tiefbau nicht.</b>
              <div className="klein" style={{ fontWeight: 400, marginTop: 4 }}>
                Zurückgemeldet wird am eigenen Tiefbau. Was dir auffällt,
                gehört in die Auftragsinfo - dort liest der Innendienst mit.
              </div>
            </div>
          ) : (
            <>
              <div className="hinweis" style={{ marginTop: 0 }}>
                Diese 4 Angaben stammen aus der Planung und weichen draußen ab.
                Eingetragen wird, was du <b>wirklich vorgefunden</b> hast -
                der geplante Wert bleibt daneben stehen. Was du leer lässt,
                gilt weiter wie geplant.
              </div>

              <TechnikFelder stand={d} wert={e} setze={setE} fehler={fehler} />

              {/* WAS DER TIEFBAU SONST NOCH VORGEFUNDEN HAT.
                  Dasselbe Bauteil wie im Fenster „Fertig melden" - 2
                  Abschriften liefen beim ersten Nachtrag auseinander, und
                  dann fragt dasselbe Formular je nach Weg etwas anderes ab. */}
              <Vorgefunden stand={d} wert={vorgefunden}
                           setze={setVorgefunden} />

              <div className="reihe" style={{ marginTop: 12 }}>
                <button className="knopf haupt" disabled={laeuft}
                        onClick={melden}>
                  {laeuft ? 'Einen Moment' : 'Zurückmelden'}
                </button>
                <button className="knopf" disabled={laeuft} onClick={zu}>
                  Abbrechen
                </button>
              </div>
            </>
          )}
        </>
      )}
    </Dialog>
  );
}


// ============================================================================
// 2. „Auftrag nicht erledigt"
// ============================================================================

/**
 * ANGEKLICKT STATT GESCHRIEBEN.
 *
 * Die Gründe kommen aus der Konfiguration und werden hier NICHT nachgebaut -
 * Marvin nennt drei und schreibt „wie zb.". Was nicht in der Liste steht,
 * kommt in die Bemerkung; **eine Liste, die einen Fall nicht kennt, darf
 * niemanden aufhalten, der vor einem verschlossenen Keller steht.**
 */
export function NichtErledigtDialog({
  auftragId, baustelle, zu, fertig,
}: {
  auftragId: string; baustelle?: string; zu: () => void; fertig: () => void;
}) {
  const [d, setD] = useState<any>(null);
  const [gruende, setGruende] = useState<string[]>([]);
  const [bemerkung, setBemerkung] = useState('');
  const [grund, setGrund] = useState('');
  const [zurueckId, setZurueckId] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);

  useEffect(() => {
    api.nichtErledigt(auftragId)
      .then(setD)
      .catch((f) => setFehler(ausFehler(f)));
  }, [auftragId]);

  async function melden() {
    setLaeuft(true); setFehler(null);
    try {
      await api.nichtErledigtMelden(auftragId,
        { gruende, bemerkung });
      fertig();
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  async function zurueck() {
    if (!zurueckId) return;
    setLaeuft(true); setFehler(null);
    try {
      await api.nichtErledigtZurueck(auftragId, zurueckId, grund);
      fertig();
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  const offene = (d?.meldungen ?? []).filter((m: any) => !m.erledigt_am);

  return (
    <Dialog titel="Auftrag nicht erledigt" untertitel={baustelle} breite={640}
            laeuft={laeuft} zu={zu}>
      {!d ? <Leer text="Wird geladen." /> : (
        <>
          <Fehlerkasten lage={fehler} />

          {/* WAS SCHON OFFEN STEHT. Eine zweite Meldung für dasselbe Gewerk
              weist der Server ab: zwei offene für dieselbe Baustelle sind
              zwei Menschen, die aufeinander warten. */}
          {!zurueckId && offene.length > 0 && (
            <div className="hinweis warnung" style={{ marginTop: 0 }}>
              <b>{offene.length === 1
                ? 'Für diesen Auftrag steht eine Meldung offen:'
                : `Für diesen Auftrag stehen ${offene.length} Meldungen offen:`}</b>
              <table className="liste" style={{ marginTop: 6 }}>
                <tbody>
                  {offene.map((m: any) => (
                    <tr key={m.id} style={{ cursor: 'default' }}>
                      <td style={{ width: 110 }}>
                        {m.gewerk_art
                          ? (GEWERK_TEXT as any)[m.gewerk_art] ?? m.gewerk_art
                          : 'ohne Gewerk'}
                      </td>
                      <td>
                        {(m.gruende ?? []).join(', ')
                          || <span className="leise">ohne Haken</span>}
                        {m.bemerkung ? ` - ${m.bemerkung}` : ''}
                      </td>
                      <td className="klein leise" style={{ width: 200 }}>
                        {zeitpunkt(m.gemeldet_am)}
                        {m.verfasser_name ? `, ${m.verfasser_name}` : ''}
                      </td>
                      <td className="knopfleiste" style={{ width: 130 }}>
                        <button className="knopf klein" disabled={laeuft}
                                onClick={() => setZurueckId(m.id)}>
                          zurücknehmen
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ZURÜCKGENOMMEN HEISST NICHT GELÖSCHT. Eine Meldung, die spurlos
              verschwindet, ist eine, die hinterher niemand erklären kann. */}
          {!zurueckId
            && (d.meldungen ?? []).some((m: any) => m.erledigt_am) && (
            <p className="klein leise">
              Zurückgenommen:{' '}
              {(d.meldungen ?? []).filter((m: any) => m.erledigt_am)
                .map((m: any) => `${(m.gruende ?? []).join(', ') || 'ohne Haken'}`
                  + ` (${m.erledigt_grund})`).join('; ')}
            </p>
          )}

          {zurueckId ? (
            <>
              <p className="klein" style={{ marginTop: 0 }}>
                Der Auftrag bekommt dabei den Bearbeitungsstand zurück, den er
                vor der Meldung hatte - sofern keine zweite mehr offen steht.
              </p>
              <Text name="grund" titel="Warum geht es jetzt weiter?" pflicht
                    mehrzeilig wert={grund} setze={setGrund} fehler={fehler}
                    hinweis="Wer die Baustelle wieder aufmacht, schuldet dem,
                             der sie angehalten hat, den Satz warum. Er steht
                             danach in der Auftragsinfo und ist für die
                             Partnerfirma sichtbar." />
              <div className="reihe" style={{ marginTop: 12 }}>
                <button className="knopf haupt"
                        disabled={laeuft || !grund.trim()} onClick={zurueck}>
                  {laeuft ? 'Einen Moment' : 'Meldung zurücknehmen'}
                </button>
                <button className="knopf" disabled={laeuft}
                        onClick={() => setZurueckId(null)}>
                  Abbrechen
                </button>
              </div>
            </>
          ) : (
            <>
              <HakenReihe titel="Was war Sache?"
                          optionen={(d.gruende ?? []).map((g: string) =>
                            ({ wert: g, text: g }))}
                          wert={gruende} setze={setGruende}
                          /* EIN PFLICHTFELD OHNE AUSWAHL IST EINE SACKGASSE -
                             dieselbe Zeile wie beim Einblasstop seit .120.
                             Hier hält es niemanden auf: die Bemerkung genügt.
                             Der Satz sagt trotzdem, wo die Liste steht. */
                          hinweis={(d.gruende ?? []).length
                            ? 'Mehrere Haken sind erlaubt. Was nicht dabei '
                              + 'steht, kommt in die Bemerkung.'
                            : 'Es stehen keine Gründe zur Auswahl. Sie werden '
                              + 'in der Konfiguration unter „Auftrag nicht '
                              + 'erledigt" gepflegt '
                              + '(montage.nicht_erledigt_gruende). Solange '
                              + 'genügt die Bemerkung.'} />
              {/* EINBLASSTOP ALS HAKEN (.281): der Haken hält an, aber Meter
                  und Richtung stehen nur im Einblasstop - und nur von dort
                  geht die Meldung an die Telekom. */}
              {gruende.includes('Einblasstop') && (
                <div className="hinweis warnung" style={{ marginTop: 6 }}>
                  Meter und Richtung bitte unter „Einblasstop melden"
                  eintragen. Von dort geht auch die Meldung mit dem
                  Einblasprotokoll an die Telekom, und „nicht erledigt" steht
                  dann von selbst hier.
                </div>
              )}

              {/* KEIN GEWERKFELD MEHR.
                  ORIGINALTEXT MARVIN (08.09.2026)
                    „das thema welches gewerk brauchen wir nicht, jeder meldet
                     immer unter seinem gewerk, seiner firma"
                  Der Server leitet es aus dem Anmelder ab. Ein Feld, dessen
                  Wert der Server ohnehin besser weiss, ist eine Frage, deren
                  Antwort niemand liest. */}

              <Text name="bemerkung" titel="Was gehört noch dazu?" mehrzeilig
                    wert={bemerkung} setze={setBemerkung} fehler={fehler}
                    hinweis="Alles, was kein Haken hergibt - eine Rufnummer,
                             ein Name, eine Uhrzeit." />

              <div className="hinweis warnung">
                Der Auftrag geht damit auf <b>„unterbrochen"</b> und steht in
                der Glocke des Innendienstes, bis jemand die Meldung
                zurücknimmt.
              </div>

              <div className="reihe" style={{ marginTop: 12 }}>
                <button className="knopf haupt"
                        disabled={laeuft || (!gruende.length && !bemerkung.trim())}
                        onClick={melden}>
                  {laeuft ? 'Einen Moment' : 'Nicht erledigt melden'}
                </button>
                <button className="knopf" disabled={laeuft} onClick={zu}>
                  Abbrechen
                </button>
              </div>
              {/* EIN GRAUER KNOPF NENNT SEINEN GRUND NEBEN SICH. */}
              {!gruende.length && !bemerkung.trim() && (
                <p className="klein leise" style={{ marginTop: 6 }}>
                  Es fehlt: mindestens ein Haken oder ein Satz. Ohne beides
                  weiß der Innendienst danach genauso wenig wie vorher.
                </p>
              )}
            </>
          )}
        </>
      )}
    </Dialog>
  );
}


// ============================================================================
// 3. Die Kolonne ist abgefahren, die Unterlagen kommen nach
// ============================================================================

/**
 * ORIGINALTEXT MARVIN (15.09.2026)
 *   „die partner wünschen sich noch eine funktion, wo sie den auftrag per
 *    knopf fertig melden können, was wir im innendienst sehen, aber sie das
 *    aufmaß und die unterlagen später hochladen. baue das mit ein."
 *
 * **SIE ERSETZT „FERTIG MELDEN" NICHT, UND DAS IST DER GANZE BAU.** Dort wird
 * das Gewerk abgehakt, und daran hängt alles Weitere: Rotberichtigung,
 * Abrechnung, Kapazitätszahlen - dafür müssen die Pflichtordner gefüllt sein,
 * und dieser Riegel bleibt unangetastet. Diese Meldung sagt nur, dass draussen
 * nichts mehr zu tun ist; bis dahin weiss der Innendienst nicht, ob auf der
 * Baustelle noch jemand steht oder ob nur Papiere fehlen.
 *
 * **KEIN GEWERKFELD.** Der Server leitet es aus dem Anmelder ab, und nur bei
 * einem eindeutigen Paar - dieselbe Zeile wie bei „Auftrag nicht erledigt"
 * seit dem 08.09.2026: ein Feld, dessen Wert der Server ohnehin besser weiss,
 * ist eine Frage, deren Antwort niemand liest.
 *
 * **UND SIE ÄNDERT DEN BEARBEITUNGSSTAND NICHT.** Anders als „Auftrag nicht
 * erledigt", das auf „unterbrochen" setzt: hier geht die Arbeit weiter, es
 * fehlen nur die Unterlagen.
 */
export function BaustelleFertigDialog({
  auftragId, baustelle, zu, fertig,
}: {
  auftragId: string; baustelle?: string; zu: () => void; fertig: () => void;
}) {
  const [d, setD] = useState<any>(null);
  const [bemerkung, setBemerkung] = useState('');
  const [grund, setGrund] = useState('');
  const [zurueckId, setZurueckId] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);

  useEffect(() => {
    api.baustelleFertig(auftragId)
      .then(setD)
      .catch((f) => setFehler(ausFehler(f)));
  }, [auftragId]);

  async function melden() {
    setLaeuft(true); setFehler(null);
    try {
      await api.baustelleFertigMelden(auftragId, { bemerkung });
      fertig();
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  async function zurueck() {
    if (!zurueckId) return;
    setLaeuft(true); setFehler(null);
    try {
      await api.baustelleFertigZurueck(auftragId, zurueckId, grund);
      fertig();
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  const offene = (d?.meldungen ?? []).filter((m: any) => !m.erledigt_am);

  return (
    <Dialog titel="Baustelle fertig" untertitel={baustelle} breite={640}
            laeuft={laeuft} zu={zu}>
      {!d ? <Leer text="Wird geladen." /> : (
        <>
          <Fehlerkasten lage={fehler} />

          {/* WAS SCHON OFFEN STEHT. Eine zweite Meldung für dasselbe Gewerk
              weist die DATENBANK ab (2 Teilindizes seit Migration 251): 2
              offene für dieselbe Baustelle sind 2 Menschen, die aufeinander
              warten. Der Knopf daneben ist deshalb kein Ersatz für den
              Riegel, sondern die Höflichkeit davor. */}
          {!zurueckId && offene.length > 0 && (
            <div className="hinweis warnung" style={{ marginTop: 0 }}>
              <b>{offene.length === 1
                ? 'Für diesen Auftrag steht eine Fertigmeldung offen:'
                : `Für diesen Auftrag stehen ${offene.length} Fertigmeldungen `
                  + 'offen:'}</b>
              <table className="liste" style={{ marginTop: 6 }}>
                <tbody>
                  {offene.map((m: any) => (
                    <tr key={m.id} style={{ cursor: 'default' }}>
                      <td style={{ width: 110 }}>
                        {m.gewerk_art
                          ? (GEWERK_TEXT as any)[m.gewerk_art] ?? m.gewerk_art
                          : 'ohne Gewerk'}
                      </td>
                      <td>
                        {m.bemerkung
                          || <span className="leise">ohne Bemerkung</span>}
                      </td>
                      <td className="klein leise" style={{ width: 200 }}>
                        {zeitpunkt(m.gemeldet_am)}
                        {m.verfasser_name ? `, ${m.verfasser_name}` : ''}
                      </td>
                      <td className="knopfleiste" style={{ width: 130 }}>
                        <button className="knopf klein" disabled={laeuft}
                                onClick={() => setZurueckId(m.id)}>
                          zurücknehmen
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ZURÜCKGENOMMEN HEISST NICHT GELÖSCHT - dieselbe Regel wie
              nebenan: eine Meldung, die spurlos verschwindet, ist eine, die
              hinterher niemand erklären kann. */}
          {!zurueckId
            && (d.meldungen ?? []).some((m: any) => m.erledigt_am) && (
            <p className="klein leise">
              Zurückgenommen:{' '}
              {(d.meldungen ?? []).filter((m: any) => m.erledigt_am)
                .map((m: any) => `${zeitpunkt(m.erledigt_am)}`
                  + ` (${m.erledigt_grund})`).join('; ')}
            </p>
          )}

          {zurueckId ? (
            <>
              <p className="klein" style={{ marginTop: 0 }}>
                Die Meldung fällt damit aus der Glocke des Innendienstes. Am
                Bearbeitungsstand des Auftrags ändert sich nichts - sie hat ihn
                auch nicht gesetzt.
              </p>
              <Text name="grund" titel="Warum wird sie zurückgenommen?" pflicht
                    mehrzeilig wert={grund} setze={setGrund} fehler={fehler}
                    hinweis="Meistens: die Unterlagen sind da. Der Satz steht
                             danach in der Auftragsinfo und ist für die
                             Partnerfirma sichtbar - sie hat die Meldung
                             geschrieben." />
              <div className="reihe" style={{ marginTop: 12 }}>
                <button className="knopf haupt"
                        disabled={laeuft || !grund.trim()} onClick={zurueck}>
                  {laeuft ? 'Einen Moment' : 'Meldung zurücknehmen'}
                </button>
                <button className="knopf" disabled={laeuft}
                        onClick={() => setZurueckId(null)}>
                  Abbrechen
                </button>
              </div>
            </>
          ) : (
            <>
              {/* KEIN GEWERKFELD, UND KEIN PFLICHTFELD.
                  Wer draussen fertig ist, drückt einen Knopf; was er noch zu
                  sagen hat, kommt daneben. Ein Pflichtfeld hier wäre eine
                  Frage an jemanden, der gerade einpackt. */}
              <Text name="bemerkung" titel="Was gehört noch dazu?" mehrzeilig
                    wert={bemerkung} setze={setBemerkung} fehler={fehler}
                    hinweis="Freiwillig. Zum Beispiel, was noch nachkommt oder
                             wann das Aufmaß zu erwarten ist." />

              <div className="hinweis">
                <b>Sie ersetzt „Fertig melden" nicht.</b> Dort wird das Gewerk
                abgehakt, und daran hängen die Rotberichtigung, die Abrechnung
                und die Kapazitätszahlen; dafür müssen die Pflichtordner
                gefüllt sein. Diese Meldung sagt nur, dass draussen nichts mehr
                zu tun ist. Sie steht sofort in der Glocke des Innendienstes
                und fällt von selbst heraus, sobald das Gewerk wirklich fertig
                gemeldet ist.
              </div>

              <div className="reihe" style={{ marginTop: 12 }}>
                <button className="knopf haupt" disabled={laeuft}
                        onClick={melden}>
                  {laeuft ? 'Einen Moment' : 'Baustelle fertig melden'}
                </button>
                <button className="knopf" disabled={laeuft} onClick={zu}>
                  Abbrechen
                </button>
              </div>
            </>
          )}
        </>
      )}
    </Dialog>
  );
}
